import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { trackPurchase } from "../lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearCart();
          setOrderId(data.order_id);
          setState("success");
          // PRIMARY conversion for the hosted-payment path (de-duped, refresh-safe)
          try {
            const o = await api.get(`/orders/${data.order_id}`);
            trackPurchase({ transactionId: o.data.order_number || data.order_id, value: o.data.grand_total, currency: "INR" });
          } catch (e) {
            trackPurchase({ transactionId: data.order_id, currency: "INR" });
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="mx-auto text-gold animate-spin mb-4" size={44} /><h1 className="font-serif text-3xl text-navy">Confirming your payment...</h1><p className="text-muted-foreground mt-2">Please wait a moment.</p></>)}
      {state === "success" && (<>
        <CheckCircle2 className="mx-auto text-green-600 mb-4" size={54} data-testid="payment-success-icon" />
        <h1 className="font-serif text-4xl text-navy">Payment Successful!</h1>
        <p className="text-muted-foreground mt-2">Your royal feast is confirmed and heading to the kitchen.</p>
        <div className="flex gap-3 justify-center mt-7">
          <button onClick={() => navigate(`/order/${orderId}`)} className="bg-navy text-cream font-semibold px-6 py-3 rounded-full hover:bg-navy-soft transition-colors" data-testid="view-order-btn">View Order</button>
          <Link to="/account" className="border border-navy/25 text-navy font-semibold px-6 py-3 rounded-full hover:border-gold transition-colors">My Orders</Link>
        </div>
      </>)}
      {(state === "error" || state === "timeout") && (<>
        <XCircle className="mx-auto text-destructive mb-4" size={48} />
        <h1 className="font-serif text-3xl text-navy">{state === "timeout" ? "Still processing" : "Payment issue"}</h1>
        <p className="text-muted-foreground mt-2">{state === "timeout" ? "Your payment is taking longer than expected. Check My Orders shortly." : "We couldn't confirm your payment."}</p>
        <Link to="/account" className="inline-flex mt-6 bg-navy text-cream font-semibold px-6 py-3 rounded-full">Go to My Orders</Link>
      </>)}
    </div>
  );
}
